import { ReducersMapObject } from 'redux';

import { BaseAppThunkAction, baseReducers } from '@common/react/store';
import {
	ApplicationStateWithChats,
	ChatsState,
	getReducer as getChatsReducer,
} from '@common/react/components/Chat/Store/Chats';
import { PageItemState, reducer as PageStateReducer } from '@common/react/store/PageItem';
import { ServerPagesState, reducer as ServerPagesReducer } from '@common/react/store/ServerPages';
import { CommentsState, getReducer as getCommentsReducer } from '@common/react/store/Comments';
import { ItemsState as ItemsProviderStoreState, getReducer as getIPStoreReducer } from '@common/react/store/ItemsProviderStore';
import { VideoChatState, getReducer as getVideoChatReducer } from '@common/react/store/VideoChat';
import { getReducer as getStatusReducer, State as SwitcherState } from '@common/react/store/SwitcherStore';
import { PhoneCallState, getReducer as getPhoneCallReducer } from '@common/react/store/PhoneCall';
import { getReducer, ItemState } from '@common/react/store/ItemProviderWithStore';

import { CompanyFeature } from '@commonTuna/react/objects/CompanyFeature';

import { User, UserStatus, UserFavorite } from '@app/objects/User';
import { Inquiry } from '@app/objects/Inquiry';
import { Location } from '@app/objects/Location';
import { AllergiesProperties } from '@app/objects/Patient';
import { AppointmentStatus, AppointmentType } from '@app/objects/Appointment';
import { CompanySettings } from '@app/objects/Company';
import { HostOptions } from '@app/objects/HostOptions';
import { Doctor } from '@app/objects/Doctor';
import { BuildData } from '@app/objects/BuildData';
import { ProcedureGroup } from '@app/objects/ProcedureGroup';
import { SecureRoutesPropsPagesMap } from '@app/objects/SecureRoutesPropsPagesMap';
import { HiddenAppointmentDots } from '@app/objects/HiddenAppointmentDots';
import { EnumAccesses } from '@app/objects/Init';
import { FacebookOptions } from '@app/objects/FacebookOptions';
import { InstagramLoginOptions } from '@app/objects/InstagramLoginOptions';

// The top-level state object
export interface ApplicationState extends ApplicationStateWithChats<User> {
	serverPage: PageItemState<any>;
	serverPages: ServerPagesState;

	enumAccesses: ItemState<EnumAccesses>;
	routesPropsPagesMap: ItemState<SecureRoutesPropsPagesMap>;

	allergyItems: SwitcherState<AllergiesProperties>;

	appointmentTypes: ItemsProviderStoreState<AppointmentType>;
	appointmentStatuses: ItemsProviderStoreState<AppointmentStatus>;

	buildData: ItemState<BuildData>;
	hostOptions: ItemState<HostOptions>;

	chats: ChatsState;
	supportChats: ChatsState;

	comments: CommentsState<User>;

	companySettings: ItemState<CompanySettings>;
	companyFeatures: ItemsProviderStoreState<CompanyFeature>;

	doctorAppointmentPaymentService: ItemsProviderStoreState<Doctor>;

	facebookOptions: ItemState<FacebookOptions>;
	instagramLoginOptions: ItemState<InstagramLoginOptions>;

	locations: ItemsProviderStoreState<Location>;

	procedureGroups: ItemsProviderStoreState<ProcedureGroup>;

	userPortalStatuses: SwitcherState<UserStatus>;
	userStatuses: SwitcherState<UserStatus>;
	userFavorites: ItemsProviderStoreState<UserFavorite>;

	statusItems: SwitcherState<AppointmentStatus>;

	phoneCall: PhoneCallState;
	videoChat: VideoChatState;

	inquiryProcesses: ItemsProviderStoreState<Inquiry>;
	hiddenAppointmentDots: ItemsProviderStoreState<HiddenAppointmentDots>;
}

// Whenever an action is dispatched, Redux will update each top-level application state property using
// the reducer with the matching name. It's important that the names match exactly, and that the reducer
// acts on the corresponding ApplicationState property type.
export const reducers: ReducersMapObject = {
	...baseReducers,

	serverPage: PageStateReducer<any>('serverPage'),
	serverPages: ServerPagesReducer<any>('serverPages'),

	enumAccesses: getReducer<EnumAccesses>('enumAccesses'),
	routesPropsPagesMap: getReducer<SecureRoutesPropsPagesMap>('routesPropsPagesMap'),

	allergyItems: getStatusReducer<AllergiesProperties>('allergiesProperties'),

	appointmentTypes: getIPStoreReducer<AppointmentType>('appointmentTypes'),
	appointmentStatuses: getIPStoreReducer<AppointmentStatus>('appointmentStatuses'),

	buildData: getReducer<BuildData>('buildData'),
	hostOptions: getReducer<HostOptions>('hostOptions'),

	chats: getChatsReducer(),
	supportChats: getChatsReducer('supportChats'),

	comments: getCommentsReducer(),

	companySettings: getReducer<CompanySettings>('companySettings'),
	companyFeatures: getIPStoreReducer<CompanyFeature>('companyFeatures'),

	doctorAppointmentPaymentService: getIPStoreReducer<Doctor>('doctorAppointmentPaymentService'),

	facebookOptions: getReducer<FacebookOptions>('facebookOptions'),
	instagramLoginOptions: getReducer<InstagramLoginOptions>('instagramLoginOptions'),

	locations: getIPStoreReducer<Location>('locations'),

	procedureGroups: getIPStoreReducer<ProcedureGroup>('procedureGroups'),

	userPortalStatuses: getStatusReducer<UserStatus>('userPortalStatuses'),
	userStatuses: getStatusReducer<UserStatus>('userStatuses'),
	userFavorites: getIPStoreReducer<UserFavorite>('userFavorites'),

	statusItems: getStatusReducer<AppointmentStatus>('statuses'),

	phoneCall: getPhoneCallReducer(),
	videoChat: getVideoChatReducer(),

	inquiryProcesses: getIPStoreReducer<Inquiry>('inquiryProcesses'),
	hiddenAppointmentDots: getIPStoreReducer<HiddenAppointmentDots>('hiddenAppointmentDots'),
};

// This type can be used as a hint on action creators so that its 'dispatch' and 'getState' params are
// correctly typed to match your store.
export type AppThunkAction<TAction> = BaseAppThunkAction<TAction, User, ApplicationState>
